//#region Imports
import AddressSchema from "_shared/addresses/address-schema";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Individual from "marketplace/individuals/individual";
import { z } from "zod";
//#endregion

export type MarketplaceIndividualInfoJSON = z.infer<
  typeof MarketplaceIndividualInfoSchema.MarketplaceIndividualInfoZ
>;

export default class MarketplaceIndividualInfoSchema {
  public static readonly MarketplaceIndividualInfoZ = z.object({
    id: z.uuid().nullish(),
    type: z.string().nullish(),
    isVisible: z.boolean().nullish(),
    legalEntityId: z.uuid().nullish(),
    companyId: z.uuid().nullish(),
    companyName: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    description: z.string().nullish(),
    location: AddressSchema.AddressJSONZ.nullish(),
    phoneNumber: z.string().nullish(),
    school: z.string().nullish(),
    degree: z.string().nullish(),
    avatarId: z.uuid().nullish(),
    email: z.email().nullish(),
    resume: z.string().nullish(),
    professionalRegistration: z.string().nullish(),
    technicalBackground: z.string().nullish(),
    numberOfApplicationsDraftedProsecuted: z.number().nullish(),
    numberOfAllowedApplications: z.number().nullish(),
    barAdmissionYear: z.number().nullish(),
    isOnCall: z.boolean().nullish(),
    insuredAmount: z.number().nullish(),
    billingRate: z.string().nullish(),
    billingUnit: z.string().nullish(),
    potentialDiversityCandidate: z.boolean().nullish(),
    collaborates: z.boolean().nullish(),
    additionalInfo: z.string().nullish(),
    links:  z.array(z.string()).nullish(),
    professionalPublications: z.array(z.string()).nullish(),
    sampleApplications: z.array(z.string()).nullish(),
    fluentLanguages: z.array(z.string()).nullish(),
    technicalLanguages: z.array(z.string()).nullish(),
    regionsRegistered: z.array(z.string()).nullish(),
    regionsLicensed: z.array(z.string()).nullish(),
    categories: z.array(z.string()).nullish(),
    userId: z.uuid().nullish(),
    isVendorRep: z.boolean().nullish(),
    isClientRep: z.boolean().nullish(),
    hasSigningAuthority: z.boolean().nullish(),
  });

  public static validateJSON(json: unknown): MarketplaceIndividualInfoJSON {
    return parseOrThrow(MarketplaceIndividualInfoSchema.MarketplaceIndividualInfoZ, json, {
      logError: true,
      schemaName: "MarketplaceIndividualInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is MarketplaceIndividualInfoJSON {
    return MarketplaceIndividualInfoSchema.MarketplaceIndividualInfoZ.safeParse(json).success;
  }

  public static deserialize(json: MarketplaceIndividualInfoJSON): Individual {
    const { 
      id, 
      email, 
      firstName, 
      lastName, 
      userId, 
      legalEntityId, 
      isVisible, 
      insuredAmount, 
      billingRate, 
      billingUnit 
    } = json;

    if (!id) throw new Error("Individual ID is required, was not returned by the API");
    if (!userId) throw new Error("User ID is required, was not returned by the API");
    if (!legalEntityId) throw new Error("Legal Entity ID is required, was not returned by the API");
    if (!firstName) throw new Error("firstName is required, was not returned by the API");
    if (!lastName) throw new Error("lastName is required, was not returned by the API");

    const individual = Individual.fromJSON({
      ...json,
      id,
      email,
      userId,
      firstName,
      lastName,
      legalEntityId,
      isVisible: isVisible ?? false,
      insuredAmount,
      billingRate,
      billingUnit,
      companyId: json.companyId ?? undefined, 
      companyName: json.companyName ?? undefined,
      hasSigningAuthority: json.hasSigningAuthority ?? undefined,
    });
  
    if (!individual) throw new Error("Failed to deserialize Individual");
  
    return individual;
  }
}
